/**
 * useFeatures - is a part of the site switched on?
 * ------------------------------------------------
 * Today it answers one question: are offers switched on? Offers were archived
 * on 17 October 2026 (OFFERS_ENABLED in backend/config/features.js), and the
 * menus, the student's dashboard and the offer pages all need to know.
 *
 * WHY THE SERVER DECIDES, and not a setting in this folder. Two switches - one
 * here, one on the server - would be two things to remember, and the day only
 * one of them was flipped the site would show a "My offer" link to a page the
 * server refuses. One switch, read from the server, cannot disagree with
 * itself.
 *
 *   const { offersEnabled, ready } = useFeatures();
 *
 * WHILE IT IS STILL ASKING, offers count as OFF. That is the same direction
 * the server fails in: if the answer never comes, the site shows less, not a
 * link to something that may not be there. `ready` is for the one place that
 * has to wait for the real answer - a page deciding whether to send somebody
 * elsewhere.
 *
 * Asked once per visit, not once per screen. The answer only changes when the
 * server is restarted, and every screen that asks shares the same request.
 */

import { useEffect, useState } from 'react';

import { settingsApi } from '../services/api';

const OFF = { offersEnabled: false };

let answer = null; // what the server said, once it has
let asking = null; // the request in flight, shared by everybody waiting

function askServer() {
  if (!asking) {
    asking = settingsApi
      .read()
      .then((d) => {
        answer = { offersEnabled: Boolean(d.offersEnabled) };
        return answer;
      })
      .catch(() => {
        // Could not ask. Treat it as off, and let the next screen try again
        // rather than remembering a failure for the rest of the visit.
        asking = null;
        return OFF;
      });
  }
  return asking;
}

export function useFeatures() {
  const [features, setFeatures] = useState(answer);

  useEffect(() => {
    if (answer) return undefined;
    let alive = true;
    askServer().then((a) => {
      if (alive) setFeatures(a);
    });
    return () => {
      alive = false;
    };
  }, []);

  return { ...(features || OFF), ready: Boolean(features) };
}
